/* SpawnAnything — weapon and tool visuals (purely client-side; the sim is untouched).
   - glowing laser beams (ERA_WEAPON_FX tracer: 'laser', colour + width; railgun thicker and lingering)
   - flame cone instead of the swing arc for flamethrowers, projectile looks (arrows, spears, stones, plasma bolts, gas puffs)
   - tool deliveries: meteor with fire trail, catapult stone on a high arc, artillery barrage (several impacts over ~1 s),
     orbital laser from the sky with ground scorch. The real bomb command is sent when the visual lands.
   Everything is pooled (ring buffers / fixed instance counts); nothing is allocated per shot. Timing runs on the game clock
   (uGTime), so pause and slow motion apply. */
import * as THREE from 'three';
import { projStyle, kindStyle, matchShooter } from './weaponKinds.js';

const FIRE1 = [1.6, 1.0, 0.35], FIRE2 = [1.5, 0.5, 0.12], SMOKE = [0.42, 0.42, 0.44];
const OCOL = [[2.2, 1.2, 0.45], [0.55, 0.55, 0.58], [0.2, 0.22, 0.2]]; // meteor (glowing), catapult stone, artillery shell
const hexRGB = (h, k = 1) => [((h >> 16) & 255) / 255 * k, ((h >> 8) & 255) / 255 * k, (h & 255) / 255 * k];
const cssHex = s => typeof s === 'string' && s[0] === '#' ? parseInt(s.slice(1), 16) : null;

/**
 * deps: { scene, uGTime, parts: () => ({ fx, debris }), groundH(x, z), explode(x, y, z, aoe, kind) (visual only),
 *         armies: () => [A0, A1], teams (TEAMS), fxHints (ERA_WEAPON_FX), shake(a, x, z), gen: () => battle generation,
 *         onImpact(x, y, z, aoe) (sound for visual-only impacts) }
 */
export function createWeaponFx(deps) {
  const { scene, uGTime, groundH, teams, fxHints } = deps;
  const P = () => deps.parts();

  /* ---------- beams: InstancedMesh ring buffer, faded in the shader by spawn time ---------- */
  const BMAX = 768;
  const bGeo = new THREE.BoxGeometry(1, 1, 1);
  const aT0 = new THREE.InstancedBufferAttribute(new Float32Array(BMAX).fill(-1e6), 1); aT0.setUsage(THREE.DynamicDrawUsage);
  const aLife = new THREE.InstancedBufferAttribute(new Float32Array(BMAX).fill(1), 1); aLife.setUsage(THREE.DynamicDrawUsage);
  bGeo.setAttribute('aT0', aT0); bGeo.setAttribute('aLife', aLife);
  // normal alpha blending (not additive): saturated beams stay readable on bright meadows and snow as well as at night
const bMat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, fog: false, toneMapped: false });
  bMat.onBeforeCompile = sh => {
    sh.uniforms.uGT = uGTime;
    sh.vertexShader = sh.vertexShader.replace('void main() {', 'uniform float uGT; attribute float aT0; attribute float aLife; varying float vBF;\nvoid main() {')
      .replace('#include <begin_vertex>', `float bAge = uGT - aT0; float bL = abs(aLife);
  float bF = aLife > 0.0 ? sqrt(clamp(1.0 - bAge / bL, 0.0, 1.0)) : clamp(bAge / bL, 0.0, 1.0);
  bF *= step(0.0, bAge) * step(bAge, bL);
  vBF = bF;
  vec3 transformed = position * vec3(0.35 + 0.65 * bF, 0.35 + 0.65 * bF, 1.0) * step(0.001, bF);`);
    sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'varying float vBF;\nvoid main() {')
      .replace('#include <color_fragment>', '#include <color_fragment>\ndiffuseColor.a *= vBF;');
  };
  bMat.customProgramCacheKey = () => 'aw-beam';
  const beams = new THREE.InstancedMesh(bGeo, bMat, BMAX);
  beams.instanceMatrix.setUsage(THREE.DynamicDrawUsage); beams.frustumCulled = false; beams.renderOrder = 4;
  beams.setColorAt(0, new THREE.Color(1, 1, 1)); beams.instanceColor.setUsage(THREE.DynamicDrawUsage);
  { const m = new THREE.Matrix4().makeScale(0, 0, 0); for (let i = 0; i < BMAX; i++) beams.setMatrixAt(i, m); }
  scene.add(beams);
  let bHead = 0, bLo = Infinity, bHi = -1;
  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _v = new THREE.Vector3(), _s = new THREE.Vector3(), _e = new THREE.Euler();
  /** beam from (x0..) to (x1..), width w, colour rgb (may exceed 1), life s (negative = fade in over |life|) */
  function beam(x0, y0, z0, x1, y1, z1, w, r, g, b, life) {
    const i = bHead; bHead = (bHead + 1) % BMAX;
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0, L = Math.hypot(dx, dy, dz) || 0.01;
    _e.set(-Math.atan2(dy, Math.hypot(dx, dz)), Math.atan2(dx, dz), 0, 'YXZ'); _q.setFromEuler(_e);
    _m.compose(_v.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2), _q, _s.set(w, w, L));
    beams.setMatrixAt(i, _m);
    const c = beams.instanceColor.array; c[i * 3] = r; c[i * 3 + 1] = g; c[i * 3 + 2] = b;
    aT0.array[i] = uGTime.value; aLife.array[i] = life;
    if (i < bLo) bLo = i; if (i > bHi) bHi = i;
  }
  function flushBeams() {
    if (bHi < 0) return;
    const n = bHi - bLo + 1;
    beams.instanceMatrix.clearUpdateRanges(); beams.instanceMatrix.addUpdateRange(bLo * 16, n * 16); beams.instanceMatrix.needsUpdate = true;
    beams.instanceColor.clearUpdateRanges(); beams.instanceColor.addUpdateRange(bLo * 3, n * 3); beams.instanceColor.needsUpdate = true;
    for (const a of [aT0, aLife]) { a.clearUpdateRanges(); a.addUpdateRange(bLo, n); a.needsUpdate = true; }
    bLo = Infinity; bHi = -1;
  }

  /* ---------- ground scorch decals ---------- */
  const SMAX = 48, SLIFE = 14;
  const sGeo = new THREE.CircleGeometry(1, 20); sGeo.rotateX(-Math.PI / 2);
  const sT0 = new THREE.InstancedBufferAttribute(new Float32Array(SMAX).fill(-1e6), 1); sT0.setUsage(THREE.DynamicDrawUsage);
  sGeo.setAttribute('aT0', sT0);
  const sMat = new THREE.MeshBasicMaterial({ color: 0x120d0a, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  sMat.onBeforeCompile = sh => {
    sh.uniforms.uGT = uGTime;
    sh.vertexShader = sh.vertexShader.replace('void main() {', `uniform float uGT; attribute float aT0; varying float vSA; varying vec2 vSP;\nvoid main() {`)
      .replace('#include <begin_vertex>', `float sAge = uGT - aT0; vSA = step(0.0, sAge) * clamp(1.0 - sAge / ${SLIFE.toFixed(1)}, 0.0, 1.0); vSP = position.xz;
  vec3 transformed = position * step(0.001, vSA);`);
    sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'varying float vSA; varying vec2 vSP;\nvoid main() {')
      .replace('#include <color_fragment>', '#include <color_fragment>\nfloat sr = length(vSP); diffuseColor.a *= min(1.0, vSA * 1.6) * 0.85 * (1.0 - smoothstep(0.6, 1.0, sr)); diffuseColor.rgb += vec3(1.6, 0.5, 0.1) * smoothstep(0.45, 0.0, sr) * max(0.0, vSA * 8.0 - 7.0);');
  };
  sMat.customProgramCacheKey = () => 'aw-scorch';
  const scorch = new THREE.InstancedMesh(sGeo, sMat, SMAX);
  scorch.instanceMatrix.setUsage(THREE.DynamicDrawUsage); scorch.frustumCulled = false; scorch.renderOrder = 1;
  { const m = new THREE.Matrix4().makeScale(0, 0, 0); for (let i = 0; i < SMAX; i++) scorch.setMatrixAt(i, m); }
  scene.add(scorch);
  let sHead = 0;
  function addScorch(x, z, r) {
    const i = sHead; sHead = (sHead + 1) % SMAX;
    _m.compose(_v.set(x, groundH(x, z) + 0.06, z), _q.setFromAxisAngle(_s.set(0, 1, 0), Math.random() * 6.28), _s.set(r, 1, r));
    scorch.setMatrixAt(i, _m); scorch.instanceMatrix.needsUpdate = true;
    sT0.array[i] = uGTime.value; sT0.needsUpdate = true;
  }

  /* ---------- falling tool objects (meteor, catapult stone, artillery shells): few, CPU-driven ---------- */
  const OMAX = 24;
  const oMesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ toneMapped: true }), OMAX);
  oMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); oMesh.frustumCulled = false; oMesh.count = 0;
  oMesh.setColorAt(0, new THREE.Color(1, 1, 1)); oMesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
  scene.add(oMesh);
  // flight pool: kind 0 meteor, 1 stone, 2 shell
  const flights = Array.from({ length: OMAX }, () => ({ on: false, kind: 0, t0: 0, dur: 1, x0: 0, y0: 0, z0: 0, x1: 0, y1: 0, z1: 0, arc: 0, size: 1, gen: 0, aoe: 0, send: null, real: false, spin: 0 }));
  function launch(kind, t0, dur, x0, y0, z0, x1, z1, arc, size, aoe, send, real) {
    const f = flights.find(q => !q.on); if (!f) { if (send) send(); return; }
    Object.assign(f, { on: true, kind, t0, dur, x0, y0, z0, x1, y1: groundH(x1, z1), z1, arc, size, aoe, send, real, gen: deps.gen(), spin: Math.random() * 6 });
  }
  // delayed beam phases (orbital laser)
  const strikes = Array.from({ length: 8 }, () => ({ on: false, t0: 0, x: 0, z: 0, aoe: 0, phase: 0, send: null, gen: 0 }));

  /* ---------- per-group caches (weapon looks), rebuilt when the groups change ---------- */
  // weapon events carry side + 2 * group (sim.worker.js evTag): the look belongs to the group
  let cache = [];
  const laser = [], flame = [], styles = [], cands = [];
  function sync() {
    const As = deps.armies(); if (!As || !As[0]) return false;
    if (As.length === cache.length && As.every((A, g) => A === cache[g])) return true;
    cache = As.slice(); laser.length = flame.length = styles.length = cands.length = As.length;
    for (let t = 0; t < As.length; t++) {
      const A = As[t], key = A.cfg.weapon, wp = A.wp, h = fxHints[key];
      const plasma = teams[A.team ? 1 : 0] ? teams[A.team ? 1 : 0].plasma : 0x6ff0ff;
      laser[t] = h && h.tracer === 'laser' ? { c: hexRGB(cssHex(h.color) ?? plasma, 1), w: h.width || 1 } : null;
      flame[t] = !!(h && h.swing === 'fire');
      styles[t] = projStyle(key, wp, h, plasma);
      cands[t] = wp && !wp.melee && !wp.hitscan ? { proj: wp.proj, arc: !!wp.arc, pspeed: wp.pspeed || 1 } : null;
    }
    return true;
  }

  let lastGen = deps.gen();
  function clearAll() {
    aT0.array.fill(-1e6); bLo = 0; bHi = BMAX - 1; // uploaded by flushBeams() at the end of this update
    sT0.array.fill(-1e6); sT0.clearUpdateRanges(); sT0.needsUpdate = true;
    for (const f of flights) f.on = false;
    for (const q of strikes) q.on = false;
    oMesh.count = 0;
  }

  const api = {
    /** EV_TRACER: draw a laser beam for laser armies; false = caller draws its normal tracer */
    tracer(x0, y0, z0, x1, y1, z1, team) {
      if (!sync()) return false;
      const L = laser[(team | 0) >> 1]; if (!L) return false;
      const [r, g, b] = L.c, w = L.w, rail = w > 1;
      beam(x0, y0, z0, x1, y1, z1, 0.3 * w, r * 0.75, g * 0.75, b * 0.75, rail ? 0.4 : 0.11);
      beam(x0, y0, z0, x1, y1, z1, 0.1 * w, 0.75 + r * 0.25, 0.75 + g * 0.25, 0.75 + b * 0.25, rail ? 0.3 : 0.08);
      const fx = P().fx;
      for (let k = 0; k < (rail ? 6 : 2); k++) fx.spawn(x1, y1, z1, (Math.random() - 0.5) * 4, Math.random() * 3, (Math.random() - 0.5) * 4, 0.18 + Math.random() * 0.12, rail ? 0.22 : 0.14, 6, 6, 3, r * 1.6, g * 1.6, b * 1.6);
      return true;
    },
    /** EV_SWING: flame cone for flamethrowers; false = caller draws its normal swing arc */
    swing(x, z, yaw, R, h, team) {
      if (!sync() || !flame[(team | 0) >> 1]) return false;
      const fx = P().fx, g = groundH(x, z), fwdX = Math.sin(yaw), fwdZ = Math.cos(yaw);
      const ox = x + fwdX * 0.5, oz = z + fwdZ * 0.5, oy = g + h * 1.25;
      for (let i = 0; i < 18; i++) {
        const a = yaw + (Math.random() - 0.5) * 0.7, sp = R * 2.2 * (0.55 + Math.random() * 0.55), core = Math.random() < 0.4, c = core ? FIRE1 : FIRE2;
        fx.spawn(ox, oy, oz, Math.sin(a) * sp, (Math.random() - 0.3) * 1.2, Math.cos(a) * sp, 0.3 + Math.random() * 0.25, core ? 0.22 + Math.random() * 0.15 : 0.32 + Math.random() * 0.25, core ? -1 : 0, 4, core ? 1 : 2, c[0], c[1], c[2]);
      }
      for (let i = 0; i < 3; i++) fx.spawn(ox + fwdX * R * 0.8, oy + 0.3, oz + fwdZ * R * 0.8, fwdX * 1.5, 1 + Math.random(), fwdZ * 1.5, 0.9 + Math.random() * 0.5, 0.45, 0, 1.5, 2, ...SMOKE);
      return true;
    },
    /** look of a projectile from the worker stream (kind 0..9, arc flag, velocity): { L, w, r, g, b, trail } */
    projStyle(kind, arc, vx, vy, vz) {
      if (!sync()) return kindStyle(kind);
      const t = matchShooter(cands, kind, arc, vx, vy, vz);
      return t >= 0 ? styles[t] : kindStyle(kind);
    },
    /** trail particles behind a projectile (call once per projectile and frame while not paused) */
    trail(st, x, y, z) {
      const tr = st.trail; if (!tr) return;
      const fx = P().fx;
      if (tr === 'smoke') { if (Math.random() < 0.6) fx.spawn(x, y, z, (Math.random() - 0.5) * 0.4, 0.3, (Math.random() - 0.5) * 0.4, 0.7, 0.28, 0, 2, 2, ...SMOKE); }
      else if (tr === 'plasma') { if (Math.random() < 0.7) fx.spawn(x, y, z, (Math.random() - 0.5) * 0.6, (Math.random() - 0.5) * 0.6, (Math.random() - 0.5) * 0.6, 0.22, 0.2, 0, 5, 1, st.r * 0.8, st.g * 0.8, st.b * 0.8); }
      else if (tr === 'gas') { if (Math.random() < 0.55) fx.spawn(x, y, z, (Math.random() - 0.5) * 0.6, 0.2 + Math.random() * 0.4, (Math.random() - 0.5) * 0.6, 0.8 + Math.random() * 0.4, 0.35 + Math.random() * 0.2, 0, 1, 2, 0.22, 0.85, 0.12); }
    },
    /**
     * Player tool at (x, z). T = tool def ({ fx, aoe, … }); send() posts the real bomb to the sim.
     * Returns true if the visual takes over (send is called later, when it lands), false = caller sends now.
     */
    tool(key, T, x, z, send) {
      const t = uGTime.value, a = Math.random() * 6.283, fx = T && T.fx;
      if (fx === 'meteor') {
        launch(0, t, 0.9, x + Math.cos(a) * 70, groundH(x, z) + 150, z + Math.sin(a) * 70, x, z, 0, 2.6, T.aoe, send, true);
        return true;
      }
      if (fx === 'stone') {
        launch(1, t, 1.35, x + Math.cos(a) * 75, groundH(x, z) + 4, z + Math.sin(a) * 75, x, z, 38, 1.4, T.aoe, send, true);
        return true;
      }
      if (fx === 'barrage') {
        // 6 shells over ~1 s; the third one is the real strike at the target, the others are visual-only impacts around it
        const n = 6;
        for (let k = 0; k < n; k++) {
          const real = k === 2, r = real ? 0 : T.aoe * (0.45 + Math.random() * 0.6), b = Math.random() * 6.283;
          const tx = x + Math.cos(b) * r, tz = z + Math.sin(b) * r;
          launch(2, t + 0.25 + k * 0.17 + Math.random() * 0.05, 0.55, tx + Math.cos(a) * 18, groundH(tx, tz) + 90, tz + Math.sin(a) * 18, tx, tz, 0, 0.5, real ? T.aoe : 3.2, real ? send : null, real);
        }
        return true;
      }
      if (fx === 'laser') {
        const s = strikes.find(q => !q.on); if (!s) return false;
        Object.assign(s, { on: true, t0: t, x, z, aoe: T.aoe, phase: 0, send, gen: deps.gen() });
        const g = groundH(x, z);
        beam(x, g + 400, z, x, g, z, 0.3, 1.0, 0.3, 0.35, -0.45); // thin aiming beam fades in
        flushBeams();
        return true;
      }
      return false;
    },
    /** per render frame (after the sim time advanced) */
    update() {
      const t = uGTime.value, gen = deps.gen(), fx = P().fx;
      if (gen !== lastGen) { lastGen = gen; clearAll(); } // new battle / back to setup: drop lingering beams and scorch marks
      let n = 0;
      const M = oMesh.instanceMatrix.array, C = oMesh.instanceColor.array;
      for (const f of flights) {
        if (!f.on) continue;
        if (f.gen !== gen) { f.on = false; continue; }
        const u = (t - f.t0) / f.dur;
        if (u < 0) continue; // barrage shells waiting for their turn
        if (u >= 1) {
          f.on = false;
          if (f.send) f.send();
          else { deps.explode(f.x1, f.y1 + 0.3, f.z1, f.aoe, 1); if (deps.onImpact) deps.onImpact(f.x1, f.y1, f.z1, f.aoe); }
          addScorch(f.x1, f.z1, f.kind === 2 ? f.aoe * 0.45 : f.aoe * 0.7);
          continue;
        }
        const ue = f.kind === 0 ? u * u * 0.4 + u * 0.6 : u; // meteors accelerate a little
        const x = f.x0 + (f.x1 - f.x0) * ue, z = f.z0 + (f.z1 - f.z0) * ue, y = f.y0 + (f.y1 - f.y0) * ue + f.arc * 4 * u * (1 - u);
        // orientation along the flight direction (+ tumble for rocks)
        const du = 0.01, ue2 = f.kind === 0 ? (u + du) * (u + du) * 0.4 + (u + du) * 0.6 : u + du;
        const vx = (f.x1 - f.x0) * (ue2 - ue), vz = (f.z1 - f.z0) * (ue2 - ue), vy = (f.y1 - f.y0) * (ue2 - ue) + f.arc * 4 * ((u + du) * (1 - u - du) - u * (1 - u));
        _e.set(-Math.atan2(vy, Math.hypot(vx, vz)) + (f.kind === 2 ? 0 : t * 5 + f.spin), Math.atan2(vx, vz), f.kind === 2 ? 0 : t * 3, 'YXZ'); _q.setFromEuler(_e);
        const sz = f.size;
        _m.compose(_v.set(x, y, z), _q, f.kind === 2 ? _s.set(sz * 0.6, sz * 0.6, sz * 2.2) : _s.set(sz, sz, sz)); _m.toArray(M, n * 16);
        const col = OCOL[f.kind];
        C[n * 3] = col[0]; C[n * 3 + 1] = col[1]; C[n * 3 + 2] = col[2];
        n++;
        if (f.kind === 0) {
          for (let k = 0; k < 3; k++) { const c = Math.random() < 0.5 ? FIRE1 : FIRE2; fx.spawn(x + (Math.random() - 0.5) * sz, y + (Math.random() - 0.5) * sz, z + (Math.random() - 0.5) * sz, (Math.random() - 0.5) * 2, Math.random() * 2, (Math.random() - 0.5) * 2, 0.25 + Math.random() * 0.25, sz * (0.25 + Math.random() * 0.3), 0, 4, 1, c[0], c[1], c[2]); }
          if (Math.random() < 0.5) fx.spawn(x + (Math.random() - 0.5) * sz, y, z + (Math.random() - 0.5) * sz, (Math.random() - 0.5) * 1.5, 1, (Math.random() - 0.5) * 1.5, 0.9 + Math.random() * 0.5, sz * 0.5, 0, 1, 2, ...SMOKE);
        } else if (f.kind === 2 && Math.random() < 0.6) fx.spawn(x, y, z, 0, 0.5, 0, 0.6, 0.3, 0, 2, 2, ...SMOKE);
      }
      oMesh.count = n;
      if (n) { oMesh.instanceMatrix.needsUpdate = true; oMesh.instanceColor.needsUpdate = true; }
      // orbital laser: aim (0.45 s) -> strike: beam + sparks for 0.9 s
      for (const s of strikes) {
        if (!s.on) continue;
        if (s.gen !== gen) { s.on = false; continue; }
        const age = t - s.t0, g = groundH(s.x, s.z);
        if (s.phase === 0 && age >= 0.45) {
          s.phase = 1; if (s.send) s.send();
          beam(s.x, g + 400, s.z, s.x, g, s.z, s.aoe * 0.55, 1.0, 0.22, 0.3, 1.0);
          beam(s.x, g + 400, s.z, s.x, g, s.z, s.aoe * 0.2, 1.0, 0.95, 0.85, 0.9);
          addScorch(s.x, s.z, s.aoe * 0.95);
          if (deps.shake) deps.shake(s.aoe * 1.5, s.x, s.z);
        }
        if (s.phase === 1) {
          for (let k = 0; k < 5; k++) { const a = Math.random() * 6.283, sp = 3 + Math.random() * 7, c = Math.random() < 0.5 ? FIRE1 : FIRE2; fx.spawn(s.x + Math.cos(a) * s.aoe * 0.3, g + 0.3, s.z + Math.sin(a) * s.aoe * 0.3, Math.cos(a) * sp, 2 + Math.random() * 5, Math.sin(a) * sp, 0.3 + Math.random() * 0.3, 0.25 + Math.random() * 0.25, 9, 6, 3, c[0], c[1], c[2]); }
          if (Math.random() < 0.5) fx.spawn(s.x, g + 1, s.z, (Math.random() - 0.5) * 2, 3, (Math.random() - 0.5) * 2, 1.5, s.aoe * 0.4, 0, 1, 2, ...SMOKE);
          if (age > 1.35) s.on = false;
        }
      }
      flushBeams(); fx.flush();
    },
  };
  if (import.meta.env && import.meta.env.DEV) { globalThis.__awFx = api; api._debug = { flights, strikes, oMesh, scorch, sT0 }; }
  return api;
}
